// MobileOnboarding — onboarding TOUCH do app mobile (parede A2). 3 slides
// horizontais com SWIPE (drag-follow + snap), dots tapáveis, "Pular" e botão
// "Próximo"/"Começar". Aparece só no 1º acesso (gate `welcome-flag.ts`).
//
// COPY PRÓPRIA (decisão travada): não reusa a copy da TV; slide 3 fala de login
// direto, não de QR (QR é exclusivo da TV). Namespace i18n `mobileOnboarding`.
//
// SWIPE = pointer events NATIVOS (pointerdown/move/up + setPointerCapture). Sem
// lib externa, sem Norigin/spatial-nav (regra de isolamento da frente mobile). A
// regra de "trocou de slide ou voltou pro lugar" é a função pura resolveSwipe
// (onboarding-swipe.ts), testável sem DOM. O swipe é UM dos caminhos: dots e
// botões também navegam, então quem nunca arrasta consegue avançar mesmo assim.

import { useTranslation } from '@app-iptv/i18n'
import { useEffect, useRef, useState } from 'react'

import styles from './MobileOnboarding.module.css'
import { clampIndex, resolveSwipe } from './onboarding-swipe'

interface MobileOnboardingProps {
  /** Onboarding concluído (último "Começar" ou "Pular") — o MobileRoot marca
   *  o welcome como visto e segue pro login. */
  onDone: () => void
}

/** As 3 chaves de slide. A copy mora no i18n (`mobileOnboarding.slideN.*`); a
 *  ORDEM é o arco narrativo: promessa → conta te segue → como entrar. */
const SLIDE_KEYS = ['slide1', 'slide2', 'slide3'] as const
const SLIDE_COUNT = SLIDE_KEYS.length

export function MobileOnboarding({ onDone }: MobileOnboardingProps): JSX.Element {
  const { t } = useTranslation()
  const [index, setIndex] = useState(0)
  // Deslocamento de arraste em px durante o gesto (drag-follow). 0 = parado.
  const [dragX, setDragX] = useState(0)
  // prefers-reduced-motion: detectado no cliente (useEffect não roda no SSR/
  // render estático dos testes, então o markup inicial fica determinístico).
  // Sob reduced-motion a troca de slide é cross-fade PURO (sem translateX/drag).
  const [reducedMotion, setReducedMotion] = useState(false)
  const trackRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReducedMotion(mq.matches)
    const onChange = (e: MediaQueryListEvent): void => setReducedMotion(e.matches)
    // addEventListener é o caminho moderno; addListener é fallback p/ WebKit antigo
    // (Tizen/webOS 2017+), onde o método novo pode não existir. Detecta o método
    // UMA vez e usa o mesmo no cleanup único (evita cleanup em branch).
    const hasModern = typeof mq.addEventListener === 'function'
    if (hasModern) mq.addEventListener('change', onChange)
    else mq.addListener(onChange)
    return () => {
      if (hasModern) mq.removeEventListener('change', onChange)
      else mq.removeListener(onChange)
    }
  }, [])

  // Estado do gesto vivo. Em ref pra não disparar re-render a cada move (o move
  // só atualiza dragX, que move o track via transform).
  const gesture = useRef<{ startX: number; startT: number; active: boolean } | null>(null)

  const isLast = index === SLIDE_COUNT - 1

  const goTo = (next: number): void => {
    setIndex(clampIndex(next, SLIDE_COUNT))
    setDragX(0)
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    // Só botão primário / toque. Captura o ponteiro pra seguir fora do elemento.
    if (e.button !== 0 && e.pointerType === 'mouse') return
    gesture.current = { startX: e.clientX, startT: Date.now(), active: true }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // setPointerCapture pode falhar em ambientes sem suporte — degrada pra
      // gesto sem captura (ainda funciona enquanto o ponteiro fica sobre o track).
    }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const g = gesture.current
    if (!g || !g.active) return
    // Sob reduced-motion não há drag-follow visual (evita o salto horizontal); o
    // gesto ainda conta no endGesture pra trocar via cross-fade. dragX fica em 0.
    if (reducedMotion) return
    let delta = e.clientX - g.startX
    // Rubber-band nas bordas: resistência elástica ao arrastar pra fora do range.
    if ((index === 0 && delta > 0) || (index === SLIDE_COUNT - 1 && delta < 0)) {
      delta *= 0.35
    }
    setDragX(delta)
  }

  const endGesture = (e: React.PointerEvent<HTMLDivElement>): void => {
    const g = gesture.current
    if (!g || !g.active) return
    gesture.current = null
    const width = trackRef.current?.offsetWidth ?? e.currentTarget.offsetWidth ?? 0
    const next = resolveSwipe({
      current: index,
      count: SLIDE_COUNT,
      deltaX: e.clientX - g.startX,
      width,
      durationMs: Date.now() - g.startT,
    })
    goTo(next)
  }

  // Posição do track. Sob reduced-motion NÃO aplicamos translateX: os slides
  // empilham (CSS .trackReduced) e a troca é cross-fade de opacity — sem salto
  // horizontal. No modo normal: -index*100% (slide atual) + arraste vivo em px.
  const trackStyle: React.CSSProperties = reducedMotion
    ? {}
    : {
        transform: `translateX(calc(${-index * 100}% + ${dragX}px))`,
        // Sem transição enquanto o dedo arrasta (segue 1:1); transição no snap.
        transition: dragX !== 0 ? 'none' : undefined,
      }

  // O hint de swipe some no último slide (não há "mais" pra ir) e sob
  // reduced-motion (CSS já desliga a animação; aqui some pra não distrair).
  const swipeHintOff = isLast

  return (
    <div className={styles.screen} data-testid="mobile-onboarding">
      <button type="button" className={styles.skip} onClick={onDone}>
        {t('mobileOnboarding.skip')}
      </button>

      <div
        ref={trackRef}
        className={styles.viewport}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
      >
        <div
          className={`${styles.track} ${reducedMotion ? styles.trackReduced : ''}`}
          style={trackStyle}
        >
          {SLIDE_KEYS.map((key, i) => (
            <section
              className={styles.slide}
              key={key}
              data-slide={key}
              data-active={i === index}
              // Leitor de tela lê só o slide ativo (não os 3 de uma vez); os
              // inativos ficam fora da árvore acessível enquanto não estão na view.
              aria-hidden={i === index ? undefined : true}
            >
              <div className={styles.art} aria-hidden="true" data-art={key} />
              <h1 className={styles.title}>{t(`mobileOnboarding.${key}.title`)}</h1>
              <p className={styles.subtitle}>{t(`mobileOnboarding.${key}.subtitle`)}</p>
            </section>
          ))}
        </div>

        {/* Affordance: "tem mais, arraste". Decorativo (aria-hidden) — os dots e
            o botão "Próximo" são o caminho acessível equivalente. */}
        <div
          className={styles.swipeHint}
          data-hint={swipeHintOff ? 'off' : 'on'}
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.dots} role="tablist" aria-label={t('mobileOnboarding.dotsLabel')}>
          {SLIDE_KEYS.map((key, i) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={t('mobileOnboarding.goToSlide', { n: i + 1 })}
              className={`${styles.dot} ${i === index ? styles.dotActive : ''}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>

        <button
          type="button"
          className={styles.cta}
          onClick={() => (isLast ? onDone() : goTo(index + 1))}
        >
          {isLast ? t('mobileOnboarding.start') : t('mobileOnboarding.next')}
        </button>
      </div>
    </div>
  )
}
